import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import styled from 'styled-components';
import { Header, Grid, Divider, Flag, Popup, Icon } from 'semantic-ui-react';
import {
  Accounts,
  Alert,
  Portfolio,
  Storage,
  ReallocateAccount,
} from 'actions';
import { colors } from 'utils/colors';
import { Segment, Button, ButtonFA } from 'components';
import { getHoldTotalMarketValue, hasNegativeWeight } from 'utils/helpers';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { HoldingTable, GicTable, AssetChart } from '../components';

class MultipleAccount extends Component {
  componentDidMount() {
    const { selectedAccountList, history } = this.props;

    if (selectedAccountList.length === 0) {
      history.push('/accounts/user');
    }
  }

  getFlag = (currency) => (currency === 'USD' ? 'us' : 'ca');

  analyze = (holdings, region) => {
    const { dispatch, history, selectedAccountList } = this.props;
    const gicHoldings = holdings.filter((holding) => holding.gic_ticker);
    const concatAccountNames = selectedAccountList
      .map((acc) => acc.user_institution)
      .join(' - ');

    return dispatch(
      Accounts.analyze(
        holdings,
        history,
        { user_institution: concatAccountNames },
        gicHoldings,
        region
      )
    );
  };

  parseNum = (num) => {
    num = !num ? 0 : Math.round(num * 100) / 100;
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  render() {
    const {
      dispatch,
      history,
      selectedAccountOverview,
      selectedAccountList,
      pwpaExchangeRate,
      user_region,
    } = this.props;
    const validHoldings =
      selectedAccountOverview !== null
        ? selectedAccountOverview.holdings.filter(
            (holding) => !!holding.security_detail
          )
        : null;
    const usdHolding = validHoldings.find((holding) => {
      const price = holding.security_detail
        ? holding.security_detail.unadjusted_closing_price.USD
        : holding.unadjusted_closing_price.USD;

      return holding.default_currency === 'USD' && !!price;
    });
    const usdAccount = selectedAccountList.find(
      (account) => account.currency === 'USD'
    );
    const excludeGicHoldings =
      selectedAccountOverview &&
      selectedAccountOverview.holdings.filter((holding) => !holding.gic_ticker);
    const gicHoldings =
      selectedAccountOverview &&
      selectedAccountOverview.holdings.filter((holding) => holding.gic_ticker);
    const hasNegativeWeights = hasNegativeWeight(
      selectedAccountOverview.holdings
    );
    const isNegativePort =
      getHoldTotalMarketValue(
        selectedAccountOverview,
        pwpaExchangeRate,
        user_region
      ) < 0;

    return (
      <div>
        {selectedAccountOverview !== null &&
          selectedAccountList.length !== 0 && (
            <Segment>
              <Header
                size="large"
                content="Grouped Portfolios"
                subheader={`${selectedAccountList.length} portfolios selected`}
              />
              <Divider fitted />
              <Grid>
                <Grid.Row columns={2}>
                  <Grid.Column width={7}>
                    <div style={{ marginTop: '1.3rem' }}>
                      <div style={{ float: 'left', padding: '0 2rem 1rem 0' }}>
                        <Header
                          size="medium"
                          content="Account Value:"
                          sub
                          style={{ margin: 0, fontWeight: 700 }}
                        />
                        <Value className="main">
                          {`$${this.parseNum(selectedAccountOverview.balance)}`}
                          <Flag
                            name={
                              user_region === 'US'
                                ? 'us'
                                : this.getFlag(selectedAccountOverview.currency)
                            }
                            style={{
                              position: 'relative',
                              bottom: '7px',
                              marginLeft: 7,
                            }}
                          />
                        </Value>
                      </div>

                      <div style={{ float: 'left', padding: '0 0 1rem 0' }}>
                        <Header
                          size="medium"
                          content="Daily Change:"
                          sub
                          style={{ margin: 0 }}
                        />
                        <Value>{`${
                          selectedAccountOverview.change || 0
                        }%`}</Value>
                      </div>
                    </div>

                    <div style={{ clear: 'both' }}>
                      <Header
                        size="medium"
                        content="Holdings Value:"
                        sub
                        style={{ margin: 0 }}
                      />
                      <Value>{`$${this.parseNum(
                        selectedAccountOverview.balance -
                          selectedAccountOverview.cash
                      )}`}</Value>
                    </div>
                    <div style={{ marginTop: '1.3rem' }}>
                      <Header
                        sub
                        size="medium"
                        style={{ margin: 0 }}
                        content={<div>cash value:</div>}
                      />
                      <Value>{`$${this.parseNum(
                        selectedAccountOverview.cash
                      )}`}</Value>
                    </div>
                  </Grid.Column>
                  <Grid.Column width={9}>
                    {!selectedAccountOverview.holdings.length ||
                    !validHoldings.length ? null : (
                      <Segment basic compact>
                        <Header
                          size="medium"
                          textAlign="center"
                          style={{ margin: '0 0 1rem 0' }}
                        >
                          Net Asset Allocation
                          <Header.Subheader>
                            Includes a breakdown of the underlying holdings of
                            balanced and asset allocation funds.
                            <Popup
                              trigger={
                                <Icon
                                  name="question circle outline"
                                  style={{
                                    fontSize: '15px',
                                    verticalAlign: 'initial',
                                    color: 'dimgrey',
                                  }}
                                />
                              }
                              position="top center"
                              content="Analyze and use the Scorecard to drill down to the underlying holdings to i) see the geographical split by asset class, and ii) check fund structure (e.g., some funds use derivatives). You will find this information in Diversification → Asset Class (bottom chart)."
                              wide
                            />
                          </Header.Subheader>
                        </Header>
                        <AssetChart
                          accounts={selectedAccountList}
                          overview={selectedAccountOverview}
                        />
                      </Segment>
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              {hasNegativeWeights ? (
                isNegativePort ? (
                  <p
                    style={{
                      color: `${colors.red}`,
                      fontWeight: 'bold',
                      position: 'absolute',
                      top: '4.5rem',
                      right: '2rem',
                    }}
                  >
                    <Icon name="warning sign" style={{}} />
                    Analysis will not be performed for net short portfolio(s).
                  </p>
                ) : (
                  <p
                    style={{
                      color: `${colors.red}`,
                      fontWeight: 'bold',
                      position: 'absolute',
                      top: '4.5rem',
                      right: '2rem',
                    }}
                  >
                    <Icon name="warning sign" style={{}} />
                    Short positions will be excluded from the analysis.
                  </p>
                )
              ) : null}
              <CurrencyOption>
                <Button
                  className={cn('left', {
                    disabled: isNegativePort,
                  })}
                  icon="line chart"
                  color={colors.darkBlue}
                  onClick={() => {
                    dispatch(Storage.setItem('toggle_region', user_region));
                    this.analyze(selectedAccountOverview.holdings, user_region);
                    dispatch(Portfolio.notSaveUrlSc());
                  }}
                  style={{
                    zIndex: 1,
                  }}
                >
                  Analyze
                </Button>
                {selectedAccountOverview.cash > 0 && (
                  <div className="currency_item">
                    <StyledButton
                      className="basic"
                      color={colors.darkBlue}
                      onClick={() => history.push('/accounts/multiple/cash')}
                    >
                      <div className="flag cash">
                        <Icon
                          name="money"
                          style={{ fontSize: '1rem', margin: '0' }}
                        />
                      </div>
                      <div style={{ marginLeft: '36px' }}>Incl. Cash</div>
                    </StyledButton>
                  </div>
                )}
              </CurrencyOption>
              <MoreActions>
                <div className="trigger">
                  <Button
                    className={cn('left grey-focus2', {
                      disabled: isNegativePort,
                    })}
                    icon="ellipsis horizontal"
                    color={colors.teal}
                    style={{
                      zIndex: 1,
                      width: '137px',
                      cursor: 'default',
                      margin: 0,
                    }}
                  >
                    More
                  </Button>
                </div>
                <div className="action-item">
                  <ButtonFA
                    className="left"
                    icon={faSync}
                    color={colors.teal}
                    onClick={() => {
                      dispatch(ReallocateAccount.resetReallocate());
                      dispatch(
                        ReallocateAccount.updateParameter('selectedAccount', [
                          ...selectedAccountList,
                        ])
                      );
                      dispatch(
                        ReallocateAccount.updateParameter('currentPage', 1)
                      );
                      history.push(`/tools/portfolio-reallocate`);
                    }}
                    style={{
                      backgroundColor: 'white',
                      display: 'block',
                      width: '100%',
                    }}
                  >
                    Reallocate
                  </ButtonFA>
                </div>
              </MoreActions>
              <div style={{ clear: 'both' }} />

              <HoldingTable
                accountList={selectedAccountList}
                totalMarketValue={
                  selectedAccountOverview.balance - selectedAccountOverview.cash
                }
                holdings={excludeGicHoldings.slice(0, 10)}
                accountName=""
                title="Top 10 Holdings"
                rate={pwpaExchangeRate}
                user_region={user_region}
              />
              <GicTable
                user_region={user_region}
                gicHoldings={gicHoldings}
                totalMarketValue={
                  selectedAccountOverview.balance - selectedAccountOverview.cash
                }
                title="GIC"
                rate={pwpaExchangeRate}
              />
              {excludeGicHoldings.length !== 0 && (
                <P>
                  {user_region === 'CA' &&
                  selectedAccountOverview.currency === 'USD' ? (
                    // eslint-disable-next-line react/jsx-fragments
                    <React.Fragment>
                      For USD accounts, we only have data for U.S.-listed
                      stocks/ETFs, U.S. mutual funds, and Canadian-listed
                      securities denominated in USD.
                      <br />
                      Any other securities such as{' '}
                      <b>
                        private funds, individual bonds, and derivatives
                      </b>{' '}
                      will be excluded from the analysis.
                    </React.Fragment>
                  ) : user_region === 'CA' ? ( // eslint-disable-next-line react/jsx-fragments
                    <React.Fragment>
                      Currently, we have data for Canadian and US stocks/ETFs,
                      Canadian mutual and segregated funds, and CAD/HISA funds.
                      <Popup
                        trigger={
                          <Icon
                            name="question circle outline"
                            style={{
                              fontSize: '15px',
                              verticalAlign: 'initial',
                            }}
                          />
                        }
                        position="top center"
                        content="CAD/USD HISA funds are included using the historical rates of a representative bank-owned CAD/USD HISA fund."
                      />
                      <br />
                      Any other securities such as{' '}
                      <b>
                        private funds, individual bonds, and derivatives
                      </b>{' '}
                      will be excluded from the analysis.
                    </React.Fragment>
                  ) : (
                    'Currently we only have data for U.S. listed stocks and ETFs, U.S. mutual funds, Bitcoin, and Ethereum. Any other security will be excluded from the analysis.'
                  )}
                </P>
              )}
              {(!!usdHolding || !!usdAccount) &&
                excludeGicHoldings.length !== 0 &&
                user_region === 'CA' && (
                  <P>USD/CAD exchange rate used is {pwpaExchangeRate || 1.3}</P>
                )}
            </Segment>
          )}
      </div>
    );
  }
}

MultipleAccount.propTypes = {
  dispatch: PropTypes.func,
  history: PropTypes.object,
  selectedAccountList: PropTypes.array,
  selectedAccountOverview: PropTypes.object,
  pwpaExchangeRate: PropTypes.number,
  user_region: PropTypes.string,
};

MultipleAccount.defaultProps = {
  dispatch: () => {},
  history: {},
  selectedAccountOverview: {},
  selectedAccountList: [],
  pwpaExchangeRate: 0,
  user_region: 'CA',
};

export default connect((state) => ({
  selectedAccountList: state.Accounts.selectedAccountList,
  selectedAccountOverview: state.Accounts.selectedAccountOverview,
  pwpaExchangeRate: state.Storage['pwpa-exchange-rate'],
  user_region: state.Storage.user_region || 'CA',
}))(MultipleAccount);

const Value = styled.div`
  font-size: 1.8rem;
  padding: 7px 0;

  &.main {
    font-size: 2.4rem;
    font-weight: 700;
    color: ${colors.orange};
  }
`;

const MoreActions = styled.div`
  .action-item {
    position: absolute;
    top: 20px;
    right: 2rem;
    padding: 2rem 0 0 0;
    width: 137px;
    opacity: 0;
    visibility: hidden;
    transition: opacity 200ms ease, top 150ms ease;
    z-index: 2;
    &:before {
      content: '';
      position: absolute;
      top: 28px;
      left: 0;
      border-radius: 4px;
    }
    &:hover {
      opacity: 1;
      visibility: visible;
      top: 40px;
    }
  }
  .trigger {
    z-index: 2;
    width: 137px;
    position: absolute;
    top: 1.6rem;
    right: 2rem;
    cursor: pointer;
    transition: all 300ms ease;
    &:hover {
      & ~ .action-item {
        opacity: 1;
        visibility: visible;
        top: 40px;
      }
    }
  }
  button {
    display: block;
    margin: 5px auto;
    background-color: white;
  }
`;

const P = styled.p`
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
  margin: 0.7rem auto;
  max-width: 100%;
  line-height: 1.1rem;
  text-align: center;
`;

const CurrencyOption = styled.div`
  position: absolute;
  top: 1.6rem;
  right: 12.5rem;
  z-index: 3;
  cursor: pointer;
  &:hover {
    & .currency_item {
      opacity: 1;
      visibility: visible;
      top: 18px;
      transition: opacity 200ms ease, top 150ms ease;
    }
  }
  .currency_item {
    position: absolute;
    top: 0px;
    right: 0;
    padding: 2rem 0 0 0;
    opacity: 0;
    visibility: hidden;
  }
`;

const StyledButton = styled.div`
  position: relative;
  border-radius: 4px;
  box-shadow: 0 0 0 1px rgba(34, 36, 38, 0.15) inset;
  font-size: 0.9rem;
  display: inline-block;
  min-height: 1em;
  min-width: 113px;
  height: 36px;
  border: none;
  text-align: center;
  outline: 0;
  transition: all 0.3s ease;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.6);
  background-color: transparent;
  vertical-align: top;
  line-height: 36px !important;
  user-select: none !important;
  margin-top: 5px !important;
  background-color: white;
  &:hover {
    box-shadow: inset 0 0 0 1.5px ${colors.darkBlue} !important;
    color: rgba(0, 0, 0, 0.6);
  }
  .flag {
    height: 100%;
    position: absolute;
    color: white;
    top: 0;
    width: 36px;
    background-color: ${colors.darkBlue};
    padding-right: 36px;
    &:before {
      position: absolute;
      line-height: 1;
      font-size: 1em;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .cash {
    padding-right: 0px;
  }
`;
